import {
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  getReconcilesHistory,
  getSaleReportHistory,
  getSaleReportSummary,
} from "../services";
import {
  SaleReportHistory,
  SalesReportSummary,
} from "../types/sale-reports.type";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import DetailsHeader from "../components/Report/Details/Header";
import ReportDetailsList from "../components/Report/Details/ReportDetailsList";
import theme from "../theme";
import DetailsFooter from "../components/Report/Details/Footer";
import InvoiceDetailsList from "../components/Report/Details/InvoiceDetailsList";
import ReportDetailsSkeleton from "../components/Report/Details/ReportDetailsSkeleton";
import ImageModal from "../components/Report/Details/ImageModal";
import NoteSummaryBoxes from "../components/Report/Details/NoteSummaryBoxes";
import SummaryBox from "../components/Report/Details/SummaryBox";
import { SALE_REPORT_STATUS } from "../components/Report/Details/reconcile";
import PriceLadderButton from "../components/Report/Details/PriceLadderButton";
import { useInvoiceWasteSummary } from "../components/Report/Details/useInvoiceWasteSummary";
import { COMPACT_QUERY } from "../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

export type TabValue = "report" | "invoice";

function TabPanel({
  children,
  value,
  current,
}: {
  children: ReactNode;
  value: TabValue;
  current: TabValue;
}) {
  return current === value ? (
    <Box sx={{ p: 2, [compact]: { p: 1 } }}>{children}</Box>
  ) : null;
}

const tabButtonSx = (selected: boolean) => ({
  minWidth: 120,
  borderRadius: "20px",
  fontWeight: 700,
  bgcolor: selected ? theme.palette.primary.main : "transparent",
  color: selected ? "#fff !important" : "#aeaeae",
  mx: 0.5,
});

export default function ReportDetailsPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  const tabQuery = searchParams.get("tab") as TabValue;
  const navigate = useNavigate();
  const location = useLocation();

  const [tab, setTab] = useState<TabValue>("report");
  const [reportSummary, setReportSummary] = useState<SalesReportSummary>();
  const [versionHistory, setVersionHistory] = useState<string>("");
  const [reportHistory, setReportHistory] = useState<SaleReportHistory[]>();
  const [selectedHistory, setSelectedHistory] = useState<SaleReportHistory>();
  const [triggerFetch, setTriggerFetch] = useState<number>(0);
  const [openModalImage, setOpenModalImage] = useState<boolean>(false);

  const handleQueryParam = useCallback(() => {
    if (!tabQuery) return;
    if (tabQuery !== tab) {
      // report and reconcile use different version namespaces, so switching tabs
      // must clear the shared version/selection from the previous tab. Otherwise
      // the new tab fetches with the old tab's version — e.g. the invoice tab
      // firing invoice-waste?version=<sale-report-version> (which 404s) before
      // the reconcile history resolves. This mirrors handleChange (manual tab
      // click), which already resets these.
      setTab(tabQuery);
      setSelectedHistory(undefined);
      setVersionHistory("");
    }
    navigate(location.pathname, { replace: true });
  }, [location.pathname, navigate, tabQuery, tab]);

  const handleChangeVersion = useCallback((history: SaleReportHistory) => {
    setSelectedHistory(history);
    setVersionHistory(history.version);
  }, []);

  const onGetReportHistory = useCallback(
    async (uuid: string) => {
      const isSaleReport = tab === "report";
      const {
        data: { data },
      } = isSaleReport
        ? await getSaleReportHistory(uuid)
        : await getReconcilesHistory(uuid);
      setReportHistory(data.histories);

      const latestHistory = data.histories.reduce(
        (prev: SaleReportHistory, current: SaleReportHistory) =>
          Number(current.version) > Number(prev.version) ? current : prev,
      );
      handleChangeVersion(latestHistory);
    },
    [tab, handleChangeVersion],
  );

  // Resolve the report version first (independent of the summary) so that the
  // summary / list requests below never fire without a concrete ?version.
  useEffect(() => {
    if (!reportId) return;
    const uuid =
      tab === "report" ? reportId : (reportSummary?.waste_checker_id ?? "");
    if (uuid) onGetReportHistory(uuid);
    // reportSummary?.waste_checker_id is read for the invoice tab but is already
    // present (resolved on the report tab) by the time the user switches tabs,
    // so it is intentionally excluded from the deps to avoid a duplicate fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, tab, triggerFetch, onGetReportHistory]);

  const onGetSaleReportSummary = useCallback(
    async (uuid: string, version: string) => {
      const {
        data: { data },
      } = await getSaleReportSummary(uuid, version);
      setReportSummary(data);
      handleQueryParam();
    },
    [handleQueryParam],
  );

  useEffect(() => {
    if (reportId && tab === "report" && versionHistory) {
      onGetSaleReportSummary(reportId, versionHistory);
    }
  }, [triggerFetch, tab, reportId, versionHistory, onGetSaleReportSummary]);

  const handleChange = (_: SyntheticEvent, newValue: TabValue) => {
    setTab(newValue);
    setSelectedHistory(undefined);
    setVersionHistory("");
  };

  // One ladder for both tabs, fetched here because the icon that opens it sits next to
  // the tab bar rather than inside either panel.
  //
  // `versionHistory` is a reconcile_sk only on the ใบจ่ายสินค้า tab; on รายงานการขาย it
  // holds a sales-report version, a different namespace this endpoint would silently
  // answer for another day. So that tab passes nothing and lets the API resolve the
  // active reconcile — which is the one it is showing. See useInvoiceWasteSummary.
  const { summary: priceLadder, loading: priceLadderLoading } =
    useInvoiceWasteSummary(
      reportId,
      tab === "invoice" ? versionHistory : undefined,
      triggerFetch,
    );

  if (!reportSummary) return <ReportDetailsSkeleton />;

  return (
    <>
      <Box sx={{ padding: "8px 0" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Link
            to="/report/sale"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              รายงาน
            </Typography>
          </Link>
          <Typography variant="body1" sx={{ mx: 1 }}>
            /
          </Typography>
          <Typography variant="body1" color="primary">
            {reportSummary.sales_report_code}
          </Typography>
        </Box>
        {reportSummary.status ===
          SALE_REPORT_STATUS.BRANCH_ACCOUNT_REQUEST_CHANGE &&
          reportSummary.review_note && (
            // The whole point of the send-back: the finance user opening the
            // report must see what to fix without chasing the accountant.
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>บัญชีขอแก้ไข</AlertTitle>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {reportSummary.review_note}
              </Typography>
            </Alert>
          )}
        <Paper sx={{ marginBottom: "12px" }}>
          <DetailsHeader
            reportId={reportId}
            reportSummary={reportSummary}
            isReport={tab === "report"}
            setTriggerFetch={setTriggerFetch}
            setOpenModalImage={setOpenModalImage}
          />
          <Box sx={{ width: "100%", position: "relative" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                [compact]: { flexWrap: "wrap", gap: 1 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Tabs
                  value={tab}
                  onChange={handleChange}
                  sx={{
                    mb: 1,
                  }}
                  slotProps={{
                    indicator: { sx: { display: "none" } },
                  }}
                >
                  <Tab
                    label="รายงานการขาย"
                    value="report"
                    sx={tabButtonSx(tab === "report")}
                  />
                  <Tab
                    label="ใบจ่ายสินค้า"
                    value="invoice"
                    sx={tabButtonSx(tab === "invoice")}
                  />
                </Tabs>
                <PriceLadderButton
                  summary={priceLadder}
                  loading={priceLadderLoading}
                />
              </Box>
              {tab === "report" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    flexWrap: "wrap",
                    gap: "16px",
                    mr: "16px",
                    [compact]: { gap: "8px", mr: 0 },
                  }}
                >
                  {reportSummary.total_bill_count !== undefined && (
                    <SummaryBox
                      title="จำนวนบิลทั้งหมด"
                      total={reportSummary.total_bill_count}
                      unit="บิล"
                    />
                  )}
                  {reportSummary.customer_count !== undefined && (
                    <SummaryBox
                      title="จำนวนลูกค้าทั้งหมด"
                      total={reportSummary.customer_count}
                      unit="ราย"
                    />
                  )}
                  {reportSummary.total_before_vat !== undefined && (
                    <SummaryBox
                      title="ยอดก่อนภาษีมูลค่าเพิ่ม"
                      total={reportSummary.total_before_vat}
                      unit="บาท"
                    />
                  )}
                  {reportSummary.total_vat !== undefined && (
                    <SummaryBox
                      title="ภาษีมูลค่าเพิ่ม (VAT)"
                      total={reportSummary.total_vat}
                      unit="บาท"
                    />
                  )}
                  {reportSummary.total_price !== undefined && (
                    <SummaryBox
                      title="ยอดรวมทั้งสิ้น"
                      total={reportSummary.total_price}
                      unit="บาท"
                    />
                  )}
                  <NoteSummaryBoxes notes={reportSummary.credit_debit_note} />
                </Box>
              )}
            </Box>
            <TabPanel value="report" current={tab}>
              <ReportDetailsList
                reportSummary={reportSummary}
                versionHistory={versionHistory}
                setTriggerFetch={setTriggerFetch}
              />
            </TabPanel>
            <TabPanel value="invoice" current={tab}>
              <InvoiceDetailsList
                reportId={reportId}
                reconcileId={reportSummary.waste_checker_id}
                reportCode={reportSummary.sales_report_code}
                versionHistory={versionHistory}
                triggerFetch={triggerFetch}
                saleReportStatus={reportSummary.status}
                wasteSummary={priceLadder}
              />
            </TabPanel>
          </Box>
        </Paper>
        <DetailsFooter
          tab={tab}
          reportSummary={reportSummary}
          reportId={reportId}
          reportHistory={reportHistory}
          selectedHistory={selectedHistory}
          onChangeVersion={handleChangeVersion}
          setTriggerFetch={setTriggerFetch}
        />
      </Box>
      {reportSummary.additional_pictures &&
        reportSummary.additional_pictures.length !== 0 && (
          <ImageModal
            open={openModalImage}
            images={reportSummary.additional_pictures}
            handleClose={() => setOpenModalImage(false)}
          />
        )}
    </>
  );
}
